// ============================================================================
// Hooks for the case-security junction (Phase 5.6)
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { caseSecurityService } from 'api/services/caseSecurityService';
import type {
  CaseSecurity,
  SetPrimaryCaseSecurityRequest,
  UpdateCaseSecurityScopeRequest,
} from 'api/types/caseSecurity.types';

export const useCaseSecurities = (caseId: string | null | undefined) => {
  const [data, setData] = useState<CaseSecurity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true); setError(null);
    try { setData(await caseSecurityService.getByCase(caseId)); }
    catch (e) { setError(e as Error); setData([]); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, error, refetch: fetch };
};

export const useInScopeCaseSecurities = (caseId: string | null | undefined) => {
  const [data, setData] = useState<CaseSecurity[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    if (!caseId) { setData([]); return; }
    setLoading(true);
    try { setData(await caseSecurityService.getInScope(caseId)); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { void fetch(); }, [fetch]);
  return { data, loading, refetch: fetch };
};

export const useUpdateCaseSecurityScope = () => {
  const [loading, setLoading] = useState(false);
  const updateScope = useCallback(async (id: string, body: UpdateCaseSecurityScopeRequest) => {
    setLoading(true);
    try { return await caseSecurityService.updateScope(id, body); }
    finally { setLoading(false); }
  }, []);
  return { updateScope, loading };
};

export const useSetPrimaryCaseSecurity = () => {
  const [loading, setLoading] = useState(false);
  const setPrimary = useCallback(async (id: string, body: SetPrimaryCaseSecurityRequest) => {
    setLoading(true);
    try { return await caseSecurityService.setPrimary(id, body); }
    finally { setLoading(false); }
  }, []);
  return { setPrimary, loading };
};
